import React, { useEffect, useState } from 'react';
import { Box, Typography, Card, CardContent, Stack, Button, IconButton, TextField, Grid } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import DeleteIcon from '@mui/icons-material/Delete';
import { useToast } from '../ToastProvider';
import api from '../../services/api';

export default function LeaveApproverSettings() {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [userResults, setUserResults] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [priority, setPriority] = useState('1');
  const [deptResults, setDeptResults] = useState([]);
  const [scopeDeptId, setScopeDeptId] = useState('');
  const [filterDeptId, setFilterDeptId] = useState('');
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [arr, departments] = await Promise.all([
        api.get('/leave-approvers'),
        api.get('/departments')
      ]);
      setItems(arr || []);
      setDeptResults(departments || []);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data || 'Load error');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const searchUsers = async () => {
    try {
      const arr = await api.get(`/users/search?query=${encodeURIComponent(q)}&limit=20`);
      setUserResults(arr || []);
    } catch (e) {
      console.error(e); toast.error(e?.response?.data || 'User search failed');
    }
  };

  const add = async () => {
    if (!selectedUserId) { toast.error('Please select a user'); return; }
    try {
      await api.post('/leave-approvers', { userId: Number(selectedUserId), priority: Number(priority) || 1, scopeDepartmentId: scopeDeptId ? Number(scopeDeptId) : null });
      setSelectedUserId(''); setPriority('1'); setQ(''); setUserResults([]);
      setDeptResults([]); setScopeDeptId('');
      toast.success('Добавен одобряващ');
      load();
    } catch (e) {
      console.error(e); toast.error(e?.response?.data || 'Failed to add approver');
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/leave-approvers/${id}`);
      toast.success('Approver removed');
      load();
    } catch (e) {
      console.error(e); toast.error(e?.response?.data || 'Failed to remove');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>Leave Management / Approvers</Typography>
      <Card>
        <CardContent>
          <Stack direction={{ xs:'column', md:'row' }} spacing={2} alignItems="center" sx={{ flexWrap:'wrap' }}>
            <Autocomplete
              options={userResults}
              size="small"
              sx={{ minWidth: 320 }}
              getOptionLabel={(u)=> (u?.displayName ? `${u.displayName} (${u.email})` : (u?.email || ''))}
              onInputChange={(_, value)=>{ setQ(value); if (value && value.length >= 2) { clearTimeout(window.__deb_usr); window.__deb_usr = setTimeout(searchUsers, 300); } }}
              renderInput={(params) => <TextField {...params} label="User" />}
              onChange={async (_, value)=> {
                const id = value?.id || '';
                setSelectedUserId(id);
                if (!id) return;
                // 1) Validate roles
                try {
                  const roles = await api.get(`/users/${id}/roles`);
                  const roleNames = (roles||[]).map(r=>r.name);
                  const ok = roleNames.includes('DepartmentManager') || roleNames.includes('BoardManager') || roleNames.includes('SystemAdmin');
                  if (!ok) { setSelectedUserId(''); toast.error('The selected user lacks permissions to approve leave requests.'); return; }
                } catch (e) { toast.error('Грешка при проверка на роли'); return; }
                // 2) Auto-add departments where user is manager
                try {
                  const basic = await api.get(`/users/${id}/basic`);
                  const employeeId = basic?.employeeId;
                  if (employeeId) {
                    const all = await api.get('/departments');
                    const managed = (all||[]).filter(d => String(d.managerId||'') === String(employeeId));
                    for (const d of managed) {
                      try { await api.post('/leave-approvers', { userId: Number(id), priority: Number(priority) || 1, scopeDepartmentId: d.id }); } catch {}
                    }
                    // Refresh list after batch
                    load();
                  }
                } catch (e) { /* ignore */ }
              }}
            />
            <TextField select SelectProps={{ native:true }} label="Filter by department" InputLabelProps={{ shrink: true }} value={filterDeptId} onChange={(e)=>setFilterDeptId(e.target.value)} size="small" sx={{ minWidth: 220 }}>
              <option value="">— all —</option>
              {(deptResults||[]).map(d => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </TextField>
            <Autocomplete
              options={deptResults}
              size="small"
              sx={{ minWidth: 320 }}
              getOptionLabel={(d)=> d?.name || ''}
              onInputChange={async (_, value)=>{ if (value && value.length >= 1) { clearTimeout(window.__deb_dept); window.__deb_dept = setTimeout(async ()=>{ const arr = await api.get('/departments').catch(()=>[]); setDeptResults(arr||[]); }, 300); } }}
              renderInput={(params) => <TextField {...params} label="Department (scope)" />}
              onChange={(_, value)=> setScopeDeptId(value?.id || '')}
            />
            {/* Additional department (simple selector) */}
            <TextField select label="Additional department" value={scopeDeptId} onChange={(e)=>setScopeDeptId(e.target.value)} size="small" sx={{ minWidth: 240 }}>
              <option value="">— Global (Board) —</option>
              {(deptResults||[]).map(d => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </TextField>
            <TextField label="Priority" type="number" value={priority} onChange={(e)=>setPriority(e.target.value)} size="small" sx={{ width: 120 }} />
            <Button variant="contained" onClick={add}>Add</Button>
          </Stack>
        </CardContent>
      </Card>

      <Box sx={{ mt: 3 }}>
        <Stack direction={{ xs:'column', md:'row' }} spacing={2} alignItems="center" sx={{ mb: 1, flexWrap:'wrap' }}>
          <Typography variant="subtitle1" sx={{ mr: 'auto' }}>Current approvers</Typography>
          <TextField select label="Filter by department" value={filterDeptId} onChange={(e)=>setFilterDeptId(e.target.value)} size="small" sx={{ minWidth: 240 }}>
            <option value="">— all —</option>
            {(deptResults||[]).map(d => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </TextField>
          <Button variant="text" onClick={()=>setFilterDeptId('')}>Clear</Button>
        </Stack>
        <Card>
          <CardContent>
            {items?.length === 0 && <Typography color="text.secondary">No approvers configured.</Typography>}
            {items?.filter(a => !filterDeptId || String(a.scopeDepartmentId||'') === String(filterDeptId)).map(a => (
              <Stack key={a.id} direction={{ xs:'column', sm:'row' }} spacing={2} alignItems="center" sx={{ py: 1, borderBottom: '1px solid #eee' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography><strong>{a.displayName || a.email}</strong> — {a.email}</Typography>
                  <Typography variant="caption" color="text.secondary">Scope: {a.scopeDepartmentName || 'Global'} · Priority: {a.priority} · Active: {String(a.isActive)}</Typography>
                </Box>
                <IconButton color="error" onClick={()=>remove(a.id)}><DeleteIcon /></IconButton>
              </Stack>
            ))}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

